import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { ISubTopic, ISubTopicVideoLink } from '../../../../store/SubTopicSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import {
  getAccessLimitState,
  toggleLeadGenModal,
  togglePremiumModal,
} from '../../../../store/AccessLimitSlice';
import { TP_COLORS } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import {
  getYouTubeEmbedUrlByLink,
  getYouTubeVideoIdFromUrl,
  getYoutubeVideoImageThumbnailByVideoId,
} from '../../../../helper';
import { saveUserUsage } from '../../../../store/AccessLimitSlice/AccessLimitApi';

interface SubTopicVideoLibraryProps {
  subTopic: ISubTopic;
  themeMode: 'light' | 'dark';
}

const previewVideoContainerStyles: SxProps = {
  border: 'none',
  borderRadius: '10px',
  width: '100%',
  minHeight: { xs: '300px', sm: '400px', md: '400px', lg: '500px' },
  backgroundColor: 'rgba(0,0,0,0.03)',
};

const SubTopicVideoLibrary: React.FC<SubTopicVideoLibraryProps> = ({
  subTopic,
  themeMode,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  // Store
  const authState = useAppSelector(getAuthState);
  const accessLimitState = useAppSelector(getAccessLimitState);

  const authUser = authState.user;
  const videoLinks = subTopic.videoLinks || [];
  const thumbContainerStyles: SxProps = {
    backgroundColor: '#0F172A',
    borderRadius: '7px',
    padding: '15px',
    marginBottom: '15px',
  };

  if (themeMode === 'dark') {
    thumbContainerStyles.backgroundColor = '#1E293B';
  }

  const isPremiumModal =
    authUser &&
    (authUser.role.name === 'Student' || authUser.isLeadGenSubmitted);

  // State
  const [
    selectedVideoLinkItem,
    setSelectedVideoLinkItem,
  ] = useState<ISubTopicVideoLink | null>(null);

  const [isCollapseOpen, setIsCollapseOpen] = useState(true);

  useEffect(() => {
    const questyStringParams = new URLSearchParams(location.search);
    const scrollToVideos = Boolean(questyStringParams.get('toVideos'));

    if (videoLinks.length && videoLinks[0]) {
      setSelectedVideoLinkItem(videoLinks[0]);

      if (scrollToVideos) {
        const videoContainerElement = document.getElementById(
          'video-lib-container'
        );

        if (videoContainerElement) {
          setTimeout(() => {
            videoContainerElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }, 500);
        }
      }
    }
  }, []);

  const toggleCollapse = () => {
    setIsCollapseOpen((prevState) => !prevState);
  };

  const handleVideoChange = (video: ISubTopicVideoLink) => {
    if (
      accessLimitState.limitation &&
      accessLimitState.limitation.videoWatches &&
      accessLimitState.limitation.videoWatches > 0 &&
      accessLimitState.usage.videoWatches >=
        accessLimitState.limitation.videoWatches
    ) {
      dispatch(isPremiumModal ? togglePremiumModal() : toggleLeadGenModal());
    } else {
      setSelectedVideoLinkItem(video);
      if (
        authUser &&
        (authUser.role.name === 'Guest' || authUser.role.name === 'Student')
      ) {
        dispatch(
          saveUserUsage({
            userId: authUser._id,
            option: 'videoWatches',
          })
        );
      }
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '40px' }}>
        <Typography variant="h4" fontSize="30px">
          Watch related videos
        </Typography>
      </div>
      <div id="video-lib-container">
        {selectedVideoLinkItem ? (
          <div style={{ marginBottom: '5px' }}>
            <Box
              component="iframe"
              loading="eager"
              allowFullScreen
              title={selectedVideoLinkItem.title}
              src={getYouTubeEmbedUrlByLink(selectedVideoLinkItem.link)}
              sx={previewVideoContainerStyles}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            />
          </div>
        ) : null}
      </div>

      <Box sx={thumbContainerStyles}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center">
            <PlaylistPlayIcon style={{ color: TP_COLORS.white }} />
            &nbsp;
            <Typography
              color={TP_COLORS.white}
              fontWeight={500}
              fontSize="18px"
            >
              Playlist
            </Typography>
          </Stack>
          <IconButton onClick={toggleCollapse}>
            {isCollapseOpen ? (
              <ExpandLessIcon style={{ color: TP_COLORS.white }} />
            ) : (
              <ExpandMoreIcon style={{ color: TP_COLORS.white }} />
            )}
          </IconButton>
        </Stack>
        <Collapse in={isCollapseOpen}>
          <Divider
            style={{ backgroundColor: '#334155', marginBottom: '15px' }}
          />
          <Grid container spacing={2}>
            {videoLinks.map((video, index) => {
              const isSelected = selectedVideoLinkItem?._id === video._id;
              const videoId = getYouTubeVideoIdFromUrl(video.link);
              const thumb = getYoutubeVideoImageThumbnailByVideoId(
                videoId,
                'mqdefault'
              );

              const cardStyles: SxProps = {
                padding: 0,
                borderRadius: '7px',
              };

              return (
                <Grid key={index} item xs={6} sm={3} md={3} lg={2}>
                  <Card sx={cardStyles}>
                    <CardActionArea onClick={() => handleVideoChange(video)}>
                      <CardContent style={{ padding: 0 }}>
                        <img src={thumb} alt={video.title} width="100%" />
                      </CardContent>
                    </CardActionArea>
                  </Card>
                  <div style={{ padding: '5px 0' }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={500}
                      fontSize="14px"
                      color={TP_COLORS.white}
                      className="ellipsis-text two-lines"
                    >
                      {video.title}
                    </Typography>
                  </div>
                  <div style={{ padding: '0 20%' }}>
                    <Box
                      sx={{
                        marginTop: '6px',
                        width: '100%',
                        minHeight: '3px',
                        borderRadius: '4px',
                        backgroundColor: isSelected
                          ? 'primary.main'
                          : undefined,
                      }}
                    />
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </Collapse>
      </Box>
    </div>
  );
};

export default SubTopicVideoLibrary;
